import { getResourceTypeDetails, ResourceType, ResourceVariant } from 'src/utils/resource-types'
import { ProcessingStatusDto } from '../services/dtos'
import { Arn } from 'src/utils/types'

/** Returns the later of two dates. */
export function maxDate(l: Date, r: Date) {
  if (l < r) return r
  return l
}

/** Returns the earlier of two dates. */
export function minDate(l: Date, r: Date) {
  if (l < r) return l
  return r
}

/** Returns the later of two dates, handling undefined/null. Returns null if both are absent. */
export function maxOrAnyDate(l: Date | undefined, r: Date | undefined): Date | null {
  if (l && r) {
    return maxDate(l, r)
  } else if (l) {
    return l
  } else if (r) {
    return r
  } else {
    return null
  }
}

/** Returns true if the first date is later than the second, or the second is null/undefined. */
export function isLaterThan(l: Date | undefined, r: Date | undefined): boolean {
  if (l && r) {
    return l > r
  } else if (!l) {
    // l is empty/null, so can't be later than r.
    return false
  } else {
    // r must not be null.
    return true
  }
}

/** Returns the difference in milliseconds between two dates (d2 - d1). */
export function dateDiff(d2: Date, d1: Date) {
  return d2.valueOf() - d1.valueOf()
}

/** Returns the singular display name for a resource type/variant combo. */
export function getResourceTypeDisplayString(resourceType: ResourceType, resourceVariant: ResourceVariant) {
  return getResourceTypeDetails({ resourceType: resourceType, resourceVariant: resourceVariant }).displayName
}

/** Returns the plural display name for a resource type/variant combo. */
export function getResourceTypeDisplayStringPlural(resourceType: ResourceType, resourceVariant: ResourceVariant) {
  return getResourceTypeDetails({ resourceType: resourceType, resourceVariant: resourceVariant }).pluralDisplayName
}

/** This function will receive Arn as string and return a name from it */
export function getResourceNameFromArn(arn: Arn) {
  if (!arn) return
  const parts = arn.split(':')
  const terminal = parts.slice(5).join(':')
  // AWS ARNs prefix the id with its type (ec2 "vpc/vpc-0000", rds "db:name"). The
  // resources table stores the bare physical id, so strip the prefix to keep the derived
  // name matchable by the table's "contains" filter when we fall back to the ARN.
  return terminal.split('/').pop().split(':').pop()
}

/** Returns true if the resource type should be displayed in the resource selection view. */
export function resourceTypeShouldRender(resourceType: ResourceType) {
  return getResourceTypeDetails({ resourceType: resourceType }).displayForSelection
}

/** Returns true if the resource type+variant should be shown in the resource selection view. */
export function resourceTypeAndVariantShouldRender(type: ResourceType, variant: ResourceVariant) {
  return getResourceTypeDetails({ resourceType: type, resourceVariant: variant }).displayForSelection
}

/** Returns true if the resource type+variant should be shown on the resource status page. */
export function resourceTypeAndVariantVisibleInStatusPage(type: ResourceType, variant: ResourceVariant) {
  return getResourceTypeDetails({ resourceType: type, resourceVariant: variant }).displayInStatus
}

/**
 * Computes a display name for a resource, combining the friendly name and ID.
 * Shows the full name without truncation
 */
export function computeResourceDisplayName(resourceId: string, resourceName: string, resourceDisplayName?: string) {
  if (resourceDisplayName) {
    return resourceDisplayName
  }

  if (resourceId === resourceName || !resourceName || resourceName.length === 0) {
    return resourceId
  }

  return `${resourceName} (${resourceId})`
}

/** Returns the age of a date in milliseconds relative to now (or a provided reference date). */
function computeMsAge(date: Date, now: Date = null) {
  if (now === null) now = new Date()

  if (!date) {
    date = null
  }

  return now.valueOf() - date.valueOf()
}

/** Returns a human-readable "X ago" string for a given date (e.g., "5 mins ago"). */
export function timeAgoString(date: Date) {
  let secondAge = Math.round(computeMsAge(date) / 1000)
  return `${durationText(secondAge)} ago`
}

/** Converts seconds into a human-readable duration string (e.g., "5 mins", "2.3 hours", "14 days"). */
export function durationText(seconds: number, precise: boolean = false, precision: number = 3) {
  if (seconds < 60) {
    if (precise) {
      return `${seconds.toPrecision(precision)} seconds`
    }
    return 'seconds'
  }
  if (seconds < 60 * 60) {
    // within the first hour
    // express minutes with no decimal places
    let mins = Math.round(seconds / 60)
    if (mins === 1) {
      return '1 min'
    }
    return `${mins} mins`
  }
  if (seconds < 60 * 60 * 10) {
    // within the first 10 hours
    // express hours with 1 decimal place
    let hourTenths = Math.round(seconds / 60 / 6)
    if (hourTenths === 10) {
      return '1 hour'
    }
    return `${hourTenths / 10} hours`
  }
  if (seconds < 60 * 60 * 48) {
    // within the first 2 days
    // express hours with 0 decimal places
    return `${Math.round(seconds / 60 / 60)} hours`
  }
  // express as days when over 48 hours
  if (seconds < 60 * 60 * 24 * 7) {
    // within the first 7 days
    // express days with 1 decimal place
    let dayTenths = Math.round(seconds / 60 / 60 / 2.4)
    return `${dayTenths / 10} days`
  }
  // express days with 0 decimal places
  return `${Math.round(seconds / 60 / 60 / 24)} days`
}

/** Formats a date as a short localized string — time only if today, full date+time otherwise. */
export function shortTimeString(date: Date) {
  function sameDate(d1: Date, d2: Date) {
    // evaluates whether 2 Date/Time objects occur on the same date in local timezone.
    return d1.getFullYear() == d2.getFullYear() && d1.getMonth() == d2.getMonth() && d1.getDate() == d2.getDate()
  }
  try {
    let now = new Date()
    if (sameDate(now, date)) {
      // same date, so only return the time in reasonably short format
      return date.toLocaleTimeString([], { timeZoneName: 'short', minute: 'numeric', hour: 'numeric' })
    }
    return date.toLocaleString([], {
      timeZoneName: 'short',
      minute: 'numeric',
      hour: 'numeric',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    })
  } catch (err) {
    // ignore the error
  }
  // If the browser doesn't support the options parameter, just return the big ugly string.
  return date.toLocaleTimeString()
}

/** Returns true if two sets contain the same elements. */
export function equalSets<T>(a: Set<T>, b: Set<T>) {
  return a.size === b.size && new Set([...a, ...b]).size === a.size
}

/** Returns elements in set A that are not in set B. */
export function diffSets<T>(a: Set<T>, b: Set<T>) {
  return new Set(Array.from(a).filter((x) => !b.has(x)))
}

/** Returns elements that exist in both set A and set B. */
export function intersectionSets<T>(a: Set<T>, b: Set<T>) {
  return new Set(Array.from(a).filter((x) => b.has(x)))
}

/** Converts a Map<string, any> to a plain object. */
export function mapToObject(map: Map<string, any>) {
  let obj = {}
  map.forEach((value, key) => {
    obj[key] = value
  })
  return obj
}

/** Returns true if a processing operation is currently active or has a pending request newer than the last completion. */
export function processingIsActive(status: ProcessingStatusDto = null, request_timestamp: Date = null): boolean {
  if (status) {
    const latest_start = maxOrAnyDate(maxOrAnyDate(request_timestamp, status.lastRequest), status.lastStart)
    const latest_finish = maxOrAnyDate(status.lastSuccess, status.lastFailure)
    return status.active || latest_start > latest_finish
  } else {
    return false
  }
}

/** Returns the most recent completion time (success or failure) from a processing status. */
export function processingCompleteTime(status: ProcessingStatusDto) {
  return maxOrAnyDate(status.lastFailure, status.lastSuccess)
}

/** Returns true if the string is a valid IPv4 octet (numeric, 0–255). */
export function validOctet(octet: string) {
  if (!octet) {
    return false
  }
  if ([...octet].every((c) => '0123456789'.includes(c))) {
    return parseInt(octet) <= 255
  } else {
    return false
  }
}

/** Returns true if the string is a valid CIDR notation (e.g., "10.0.0.0/24"). */
export function validCidr(cidr: string) {
  const x = cidr.split('/')
  if (x.length > 2) {
    return false
  } // Can only have one '/' in a CIDR.
  const octets = x[0].split('.')
  if (octets.length != 4) {
    return false
  }
  const validMask = x.length > 1 ? validOctet(x[1]) : true
  return octets.every(validOctet) && validMask
}

const DOMAIN_REGEX = /^(?!-)\.?([A-Za-z0-9-]{1,63}(?<!-)\.)+[A-Za-z]{2,6}$/

/** Returns true if the string matches a valid domain name pattern. */
export function validDomain(domain: string) {
  return DOMAIN_REGEX.test(domain)
}

/**
 * Maps each allowlist entry the target firewall would refuse to its reason, by index, mirroring
 * arpio-api's `validate_sandbox_allowed_domains`: duplicates everywhere; on AWS also `x` + `.x` and uppercase.
 */
export function sandboxDomainIssues(domains: string[], aws: boolean): Map<number, string> {
  const issues = new Map<number, string>()
  const seen = new Map<string, string>()
  domains.forEach((domain, index) => {
    if (aws && !domain.startsWith('.') && domain !== domain.toLowerCase()) {
      issues.set(index, `"${domain}" must be lowercase`)
      return
    }
    const key = aws ? domain.toLowerCase().replace(/^\./, '') : domain
    if (seen.has(key)) {
      issues.set(index, `"${domain}" duplicates "${seen.get(key)}"`)
      return
    }
    seen.set(key, domain)
  })
  return issues
}
/**
 * Calculates the scrollable height as a percentage of the viewport (`vh`),
 * it considers header & footer height.
 *
 * @param {number} headerFooterHeight - Height of header and footer in pixels (default: 397).
 * @returns {string} - The scroll height in `vh`, rounded.
 */
export function calculateScrollHeight(headerFooterHeight: number = 397): string {
  const viewportHeight = window.innerHeight // Viewport height in pixels
  const adjustedHeight = viewportHeight - headerFooterHeight // Adjusted height in pixels
  const adjustedHeightVh = (adjustedHeight / viewportHeight) * 100
  return `${Math.round(adjustedHeightVh)}vh`
}

/** Returns true if two values are deeply equal (recursively compares objects and arrays). */
export function areDeeplyEqual(obj1, obj2) {
  if (obj1 === obj2) {
    return true
  }

  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) {
      return false
    }

    return obj1.every((elem, index) => areDeeplyEqual(elem, obj2[index]))
  }

  if (typeof obj1 === 'object' && typeof obj2 === 'object' && obj1 !== null && obj2 !== null) {
    if (Array.isArray(obj1) || Array.isArray(obj2)) {
      return false
    }

    const keys1 = Object.keys(obj1)
    const keys2 = Object.keys(obj2)

    if (keys1.length !== keys2.length || !keys1.every((key) => keys2.includes(key))) {
      return false
    }

    for (let key in obj1) {
      if (!areDeeplyEqual(obj1[key], obj2[key])) {
        return false
      }
    }

    return true
  }

  return false
}
